import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import XIcon from './icons/XIcon';
import DownloadIcon from './icons/DownloadIcon';

interface ExtractionViewerProps {
  imageUrl: string;
  onClose: () => void;
}

const ExtractionViewer: React.FC<ExtractionViewerProps> = ({ imageUrl, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [onClose]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'FuturaSmile_Denture_Extraction.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="relative w-full max-w-2xl max-h-[90vh] bg-zinc-950/80 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl shadow-black/40 flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex-shrink-0 p-4 flex justify-between items-center border-b border-white/10">
          <h2 className="text-lg font-bold text-zinc-100 flex items-center gap-3">
            Digital Denture Extraction
          </h2>
          <button onClick={onClose} className="p-2 text-zinc-400 rounded-full hover:bg-white/10 transition-colors"><XIcon className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <div className="relative p-4 rounded-lg" style={{ backgroundImage: 'linear-gradient(45deg, #3f3f46 25%, transparent 25%), linear-gradient(-45deg, #3f3f46 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #3f3f46 75%), linear-gradient(-45deg, transparent 75%, #3f3f46 75%)', backgroundSize: '20px 20px', backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px' }}>
            <img src={imageUrl} alt="Extracted Denture" className="max-w-full max-h-[60vh] object-contain" />
          </div>
        </div>

        <div className="flex-shrink-0 p-4 border-t border-white/10">
          <motion.button
            onClick={handleDownload}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 bg-gradient-to-br from-teal-400 to-teal-600 text-white border-transparent shadow-lg shadow-teal-500/20 hover:from-teal-500 hover:to-teal-600"
          >
            <DownloadIcon className="w-4 h-4" />
            Download PNG
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};
export default ExtractionViewer;
